// src/components/settings/ProfilePanel.tsx
import { useState, useEffect, useCallback, useRef } from 'react'
import {
  X, User, Shield, Eye, Trash2, Download, Calendar,
  Building2, Briefcase, Globe, Users, Save, AlertCircle,
  CheckCircle, Camera, Key, LogOut, ChevronRight
} from 'lucide-react'
import { toast } from 'react-hot-toast'
import './Settings.css'

// ✅ FIXED: Use environment variable for API URL
const API_URL = import.meta.env.VITE_API_URL || 'https://emailai-backend-658e.onrender.com/api'

interface UserProfile {
  _id?: string
  id?: string
  name: string
  email: string
  phone?: string
  company?: string
  jobTitle?: string
  industry?: string
  companySize?: string
  plan?: string
  selectedPlan?: string
  emailVerified?: boolean
  createdAt: string
  lastLogin?: string
  avatar?: string
}

interface ProfilePanelProps {
  isOpen: boolean
  onClose: () => void
  user: UserProfile | null
  onLogout: () => void
  onUserUpdate?: (updatedUser: UserProfile) => void
}

interface FormData {
  name: string
  phone: string
  company: string
  jobTitle: string
  industry: string
  companySize: string
  avatar?: string
}

interface NotificationState {
  type: 'success' | 'error'
  message: string
}

export default function ProfilePanel({ isOpen, onClose, user, onLogout, onUserUpdate }: ProfilePanelProps) {
  const [activeTab, setActiveTab] = useState<'profile' | 'privacy'>('profile')
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [notification, setNotification] = useState<NotificationState | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(user?.avatar || null)
  const [uploadingImage, setUploadingImage] = useState(false)

  // ✅ FIXED: Add CSRF token state
  const [csrfToken, setCsrfToken] = useState<string>('')

  const [formData, setFormData] = useState<FormData>({
    name: user?.name || '',
    phone: user?.phone || '',
    company: user?.company || '',
    jobTitle: user?.jobTitle || '',
    industry: user?.industry || 'other',
    companySize: user?.companySize || '1-10',
    avatar: user?.avatar || ''
  })

  // ✅ FIXED: Fetch CSRF token on component mount
  useEffect(() => {
    const fetchCsrfToken = async () => {
      try {
        const response = await fetch(`${API_URL}/csrf-token`, {
          credentials: 'include'
        })
        const data = await response.json()
        setCsrfToken(data.csrfToken)
      } catch (error) {
        console.error('Failed to fetch CSRF token:', error)
      }
    }
    
    if (isOpen) {
      fetchCsrfToken()
    }
  }, [isOpen])

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A'
    try {
      return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    } catch { return 'N/A' }
  }

  const showNotification = useCallback((type: 'success' | 'error', message: string) => {
    setNotification({ type, message })
    setTimeout(() => setNotification(null), 3000)
  }, [])

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        phone: user.phone || '',
        company: user.company || '',
        jobTitle: user.jobTitle || '',
        industry: user.industry || 'other',
        companySize: user.companySize || '1-10',
        avatar: user.avatar || ''
      })
      setProfileImageUrl(user.avatar || null)
    }
  }, [user, isOpen])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) { showNotification('error', 'Image size must be less than 5MB'); return }
    if (!file.type.startsWith('image/')) { showNotification('error', 'File must be an image'); return }
    setUploadingImage(true)
    try {
      const reader = new FileReader()
      reader.onload = async (event) => {
        const base64 = event.target?.result as string
        setProfileImageUrl(base64)
        setFormData(prev => ({ ...prev, avatar: base64 }))
        toast.success('Profile picture ready (click Save to apply)')
      }
      reader.readAsDataURL(file)
    } catch (error) {
      showNotification('error', 'Failed to process image')
    } finally {
      setUploadingImage(false)
      // ✅ FIXED: Clear input to allow re-uploading same file
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleSaveProfile = async () => {
    if (!formData.name.trim()) { showNotification('error', 'Name is required'); return }
    setIsSaving(true)
    try {
      // ✅ FIXED: Consistent token key
      const token = localStorage.getItem('accessToken')
      
      // ✅ FIXED: Use environment variable
      const response = await fetch(`${API_URL}/user/profile`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`, 
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken // ✅ FIXED: Add CSRF token
        },
        credentials: 'include', // ✅ FIXED: Include credentials for session
        body: JSON.stringify({
          name: formData.name, 
          phone: formData.phone, 
          company: formData.company,
          jobTitle: formData.jobTitle, 
          industry: formData.industry, 
          companySize: formData.companySize,
          avatar: profileImageUrl || undefined
        })
      })
      const data = await response.json()
      if (response.ok) {
  const updatedUser = { 
    ...user, 
    ...(data.data || data.user || data),
    avatar: profileImageUrl 
  };
  if (onUserUpdate) onUserUpdate(updatedUser as UserProfile);
  // Also update localStorage
  localStorage.setItem('userAvatar', profileImageUrl || '');
} else {
        showNotification('error', data.message || 'Failed to update profile')
      }
    } catch (error: any) {
      showNotification('error', error.message || 'Network error')
    } finally {
      setIsSaving(false)
    }
  }

  const handleExportData = async () => {
    try {
      const userData = { ...user, exportDate: new Date().toISOString(), profile: formData }
      const blob = new Blob([JSON.stringify(userData, null, 2)], { type: 'application/json' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `user-data-${new Date().toISOString().split('T')[0]}.json`
      a.click()
      window.URL.revokeObjectURL(url) // ✅ FIXED: Clean up object URL
      toast.success('Data exported successfully!')
    } catch (error) {
      showNotification('error', 'Failed to export data')
    }
  }

  if (!isOpen) return null

  return (
    <>
      {/* Overlay - slides from left */}
      <div className={`modal-overlay ${isOpen ? 'visible' : ''}`} onClick={onClose} />
      
      {/* Panel - slides from left */}
      <div className={`profile-panel-modal ${isOpen ? 'open' : ''}`}>
        <button className="modal-close" onClick={onClose}>
          <X className="w-5 h-5" />
        </button>
        
        <div className="modal-content">
          {/* Header */}
          <div className="profile-panel-header">
            <h2>Profile Settings</h2>
          </div>

          {/* Notification Toast */}
          {notification && (
            <div className={`profile-toast ${notification.type}`}>
              {notification.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
              <span>{notification.message}</span>
            </div>
          )}

          {/* Tabs */}
          <div className="profile-panel-tabs">
            {[{ id: 'profile' as const, label: 'Profile', icon: User }, { id: 'privacy' as const, label: 'Privacy & Security', icon: Shield }].map(tab => {
              const Icon = tab.icon
              return (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`profile-tab ${activeTab === tab.id ? 'active' : ''}`}>
                  <Icon className="w-4 h-4" />{tab.label}
                </button>
              )
            })}
          </div>

          {/* Content */}
          <div className="profile-panel-content">

            {/* PROFILE TAB */}
            {activeTab === 'profile' && (
              <div className="profile-section">
                <div className="profile-avatar-section">
                  <div className="avatar-upload">
                    <div className="avatar-preview">
                      {profileImageUrl ? <img src={profileImageUrl} alt={user?.name} /> : user?.name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <button onClick={() => fileInputRef.current?.click()} disabled={uploadingImage} className="avatar-upload-btn">
                      {uploadingImage ? <div className="spinner" /> : <Camera className="w-4 h-4" />}
                    </button>
                    <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploadingImage} />
                  </div>
                  <div className="profile-info">
                    <h3>{user?.name || 'User'}</h3>
                    <p>{user?.email}</p>
                    {user?.emailVerified && <span className="verified-badge"><CheckCircle className="w-3 h-3" /> Verified</span>}
                  </div>
                </div>

                <div className="member-since-card">
                  <Calendar className="w-5 h-5" />
                  <div><span>Member Since</span><p>{formatDate(user?.createdAt)}</p></div>
                </div>

                {isEditing ? (
                  <div className="edit-form">
                    <div className="form-grid">
                      <div className="form-field"><label>Full Name *</label><input type="text" name="name" value={formData.name} onChange={handleInputChange} /></div>
                      <div className="form-field"><label>Phone</label><input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} /></div>
                    </div>
                    <div className="form-field"><label>Email</label><input type="email" value={user?.email || ''} disabled /></div>
                    <div className="form-grid">
                      <div className="form-field"><label>Company</label><input type="text" name="company" value={formData.company} onChange={handleInputChange} /></div>
                      <div className="form-field"><label>Job Title</label><input type="text" name="jobTitle" value={formData.jobTitle} onChange={handleInputChange} /></div>
                    </div>
                    <div className="form-grid">
                      <div className="form-field">
                        <label>Industry</label>
                        <select name="industry" value={formData.industry} onChange={handleInputChange}>
                          <option value="technology">Technology</option><option value="ecommerce">E-commerce</option><option value="healthcare">Healthcare</option><option value="education">Education</option><option value="finance">Finance</option><option value="realestate">Real Estate</option><option value="other">Other</option>
                        </select>
                      </div>
                      <div className="form-field">
                        <label>Company Size</label>
                        <select name="companySize" value={formData.companySize} onChange={handleInputChange}>
                          <option value="1-10">1-10</option><option value="11-50">11-50</option><option value="51-200">51-200</option><option value="201-500">201-500</option><option value="500+">500+</option>
                        </select>
                      </div>
                    </div>
                    <div className="form-actions">
                      <button onClick={handleSaveProfile} disabled={isSaving} className="save-btn"><Save className="w-4 h-4" />{isSaving ? 'Saving...' : 'Save Changes'}</button>
                      <button onClick={() => { setIsEditing(false); if (user) { setFormData({ name: user.name || '', phone: user.phone || '', company: user.company || '', jobTitle: user.jobTitle || '', industry: user.industry || 'other', companySize: user.companySize || '1-10', avatar: user.avatar || '' }); setProfileImageUrl(user.avatar || null) } }} className="cancel-btn">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="profile-details">
                    {[{ label: 'Company', value: formData.company || '—', icon: Building2 }, { label: 'Job Title', value: formData.jobTitle || '—', icon: Briefcase }, { label: 'Industry', value: formData.industry ? formData.industry.charAt(0).toUpperCase() + formData.industry.slice(1) : '—', icon: Globe }, { label: 'Company Size', value: formData.companySize ? `${formData.companySize} employees` : '—', icon: Users }].map((item, idx) => {
                      const Icon = item.icon
                      return <div key={idx} className="detail-card"><div className="detail-content"><Icon className="w-4 h-4" /><div><span>{item.label}</span><p>{item.value}</p></div></div></div>
                    })}
                    <button onClick={() => setIsEditing(true)} className="edit-btn">Edit Profile</button>
                  </div>
                )}
              </div>
            )}

            {/* PRIVACY TAB - REMOVED Change Password and Delete Account sections */}
            {activeTab === 'privacy' && (
              <div className="privacy-section">
                <p className="privacy-desc">Control your data and privacy settings.</p>

                <div className="privacy-card">
                  <div className="privacy-card-header">
                    <Download className="w-5 h-5" />
                    <div><h4>Export Your Data</h4><p>Download a copy of all your personal data in JSON format</p></div>
                  </div>
                  <button onClick={handleExportData} className="download-btn">Download</button>
                </div>

                <div className="last-login-card">
                  <Eye className="w-5 h-5" />
                  <div><span>Last Login</span><p>{user?.lastLogin ? formatDate(user.lastLogin) : 'Just now'}</p></div>
                </div>

                <div className="privacy-note">
                  <p>🔒 <strong>Privacy:</strong> We take your privacy seriously. Your data is encrypted and never shared with third parties.</p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="profile-panel-footer">
            <button onClick={onLogout} className="logout-btn"><LogOut className="w-4 h-4" />Logout</button>
          </div>
        </div>
      </div>
    </>
  )
}